import '@expo/match-media';
import 'expo-dev-client';
import 'react-native-get-random-values';
// Polyfill crypto.randomUUID for React Native (used by @tanstack/db). Use existing uuid package.
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
  const { v4 } = require('uuid');
  crypto.randomUUID = () => v4();
}
import 'react-native-url-polyfill/auto';
import * as BackgroundTask from 'expo-background-task';
import * as SplashScreen from 'expo-splash-screen';
import * as TaskManager from 'expo-task-manager';
import Analytics from './utils/analytics';
import React, { useEffect } from 'react';
import { amplifyConfig } from './utils/environment';
import { apolloClient } from './utils/apollo-client';
import { ApolloProvider } from '@apollo/client';
import { AppContainer } from './navigators/app-container';
import { Auth, Storage } from 'aws-amplify';
import { AuthContextProvider } from './contexts/auth-context';
import {
  backgroundSyncOfflineData,
  SYNC_OFFLINE_DATA_BACKGROUND_TASK_ID,
} from './background-tasks/offline-data-sync';
import { captureException, initializeSentry } from '@/utils/sentry';
import { DemoSettingsContextProvider } from './contexts/demo-settings-context';
import { enableMapSet } from 'immer';
import { FontSource, useFonts } from 'expo-font';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { LayoutContextProvider } from './contexts/layout-context';
import { NavigationConfirmationContextProvider } from './contexts/navigation-confirmation-context';
import { NetworkStatusProvider } from './contexts/network-status-context';
import { OfflineModeSimulator } from './utils/offline-mode/offline-mode-simulator';
import { Platform, StatusBar, StyleSheet, UIManager, View } from 'react-native';
import { PortalProvider } from '@gorhom/portal';
import { queryClient } from './utils/query-client';
import { QueryClientProvider } from '@tanstack/react-query';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { SproutKitProvider } from '@usepepper/sprout-kit-ui';
import { ThemeContextProvider } from './contexts/theme-context';
import { ToastContextProvider } from './contexts/toast-context';
import { TooltipContextProvider } from './contexts/tooltip-context';

// Enable Immer MapSet plugin for zustand stores that use Set/Map
enableMapSet();

if (process.env.DEBUG_RERENDERS) {
  const wdyr = require('@welldone-software/why-did-you-render');
  wdyr(React, {
    /*
      Logging all component renders can be spammy so consider using react devtools to
      identify individual components that are rerendering and then track those instead:
      https://github.com/welldone-software/why-did-you-render#tracking-components
    */
    trackAllPureComponents: true,
  });
}

let devMenuItemsRegistered = false;

if (
  __DEV__ &&
  Platform.OS !== 'web' &&
  process.env.NODE_ENV !== 'test' &&
  !devMenuItemsRegistered
) {
  import('expo-dev-menu')
    .then(async ({ registerDevMenuItems }) => {
      // In dev mode, create and hydrate a singleton of the OfflineModeSimulator,
      // allowing other code to use a syncronous call for checking if offline mode
      // is simulated or not. This requires an app reload after enabling/disabling.
      const simulator = OfflineModeSimulator.getInstance();
      await simulator.hydrate(); // Fetches state from AsyncStorage

      const devMenuItems = [
        {
          callback: () => {
            simulator.toggleEnabled();
          },
          name: 'Simulate Offline Mode',
        },
        {
          callback: () => {
            alert(
              'Offline AsyncStorage keys have been cleared.\nPlease reload the app.',
            );
            simulator.clearOfflineAsyncStorage();
          },
          name: 'Clear offline AsyncStorage keys',
        },
      ];

      registerDevMenuItems(devMenuItems);
      devMenuItemsRegistered = true;
    })
    .catch((err) => {
      console.error('Failed to load expo-dev-menu:', err);
    });
}

Auth.configure(amplifyConfig.Auth);
Storage.configure(amplifyConfig.Storage);
Analytics.initialize({ load: false });

initializeSentry();

TaskManager.defineTask(SYNC_OFFLINE_DATA_BACKGROUND_TASK_ID, async () => {
  try {
    await backgroundSyncOfflineData();
    return BackgroundTask.BackgroundTaskResult.Success;
  } catch (e) {
    captureException(e);
    return BackgroundTask.BackgroundTaskResult.Failed;
  }
});

SplashScreen.preventAutoHideAsync().catch(() => {
  /*
   *https://github.com/expo/examples/blob/6bb49643933c87deb51e427363d2ca1ea8c3cf76/with-splash-screen/App.js#L16-L18
   *reloading the app might trigger some race conditions, ignore them
   */
});

// Required for LayoutAnimation to work on Android
// https://facebook.github.io/react-native/docs/layoutanimation.html
if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const FONT_SOURCE_MAP: Record<string, FontSource> = {
  'Geist-Medium': require('@usepepper/sprout-kit-ui/fonts/Geist-Medium'),
  'Geist-Regular': require('@usepepper/sprout-kit-ui/fonts/Geist-Regular'),
  'Geist-SemiBold': require('@usepepper/sprout-kit-ui/fonts/Geist-SemiBold'),
  'Helvetica-Black': require('../assets/fonts/Helvetica-Black.ttf'),
  'Helvetica-Regular': require('../assets/fonts/Helvetica-Regular.ttf'),
};

export const ExpoApp: React.FC = () => {
  const [fontsLoaded] = useFonts(FONT_SOURCE_MAP);

  const content = (
    <>
      <StatusBar barStyle="dark-content" translucent />
      <QueryClientProvider client={queryClient}>
        <ApolloProvider client={apolloClient}>
          <KeyboardProvider>
            <SafeAreaProvider>
              <LayoutContextProvider>
                <DemoSettingsContextProvider>
                  <ThemeContextProvider>
                    <ToastContextProvider
                      containerStyle={
                        Platform.OS === 'web' ? styles.webToastContainer : null
                      }
                    >
                      <NetworkStatusProvider>
                        <AuthContextProvider>
                          <TooltipContextProvider>
                            <SproutKitProvider>
                              <PortalProvider>
                                <NavigationConfirmationContextProvider>
                                  <AppContainer />
                                </NavigationConfirmationContextProvider>
                              </PortalProvider>
                            </SproutKitProvider>
                          </TooltipContextProvider>
                        </AuthContextProvider>
                      </NetworkStatusProvider>
                    </ToastContextProvider>
                  </ThemeContextProvider>
                </DemoSettingsContextProvider>
              </LayoutContextProvider>
            </SafeAreaProvider>
          </KeyboardProvider>
        </ApolloProvider>
      </QueryClientProvider>
    </>
  );

  useEffect(() => {
    if (fontsLoaded) {
      SplashScreen.hideAsync().catch(() => {
        // as with preventAuthHideAsync, ignore
      });
    }
  }, [fontsLoaded]);

  if (!fontsLoaded) {
    return null;
  }

  return Platform.OS === 'web' ? (
    <View style={styles.webContainer} testID="app">
      {content}
    </View>
  ) : (
    content
  );
};

const styles = StyleSheet.create({
  webContainer: {
    flex: 1,
    width: '100%',
  },
  webToastContainer: {
    top: 0,
  },
});
